import { useCallback, useMemo } from 'react';

import { fromPromise } from 'xstate';

import { useNoticeBannersStackContext } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';

import { useGroupOrderingContent } from '@order/features-group-ordering';
import { type UnlockGroupOrderResponse } from '@order/features-ordering';

import {
  type BagUnlockGroupOrderMutation,
  useBagUnlockGroupOrderMutation,
} from '../../GraphQL/BagGroupOrder.generated';
import { prepareCart } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useUnlockGroupOrder = () => {
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { mappedContent: groupOrderingContent } = useGroupOrderingContent();
  const [, executeUnlockGroupOrder] = useBagUnlockGroupOrderMutation();

  // ─── Derived ──────────────────────────────────────────────────────────────

  const unlockGroupOrder = useMemo(
    () =>
      fromPromise<UnlockGroupOrderResponse, void>(async () => {
        const response = await executeUnlockGroupOrder({});
        const result = response.data?.unlockOrder;

        if (result?.__typename === 'UnlockOrderSuccess') {
          return { cart: prepareCart(result.order as Partial<Order>) };
        }

        const responseError = getResponseError({ result }) ?? response.error?.message;

        throw new Error(responseError);
      }),
    [executeUnlockGroupOrder],
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const unlockCartInBackground = useCallback(() => {
    void executeUnlockGroupOrder({});
  }, [executeUnlockGroupOrder]);

  const handleUnlockGroupOrderError = useCallback(() => {
    addNoticeBanner({
      text: groupOrderingContent.unlockCartErrorMessage,
      palette: 'caution',
    });
  }, [addNoticeBanner, groupOrderingContent.unlockCartErrorMessage]);

  return { unlockGroupOrder, unlockCartInBackground, handleUnlockGroupOrderError };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getResponseError(params: GetResponseErrorParams) {
  const { result } = params;

  if (!result || result.__typename === 'UnlockOrderSuccess') return;

  return result.errorMessage;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type GetResponseErrorParams = {
  result: BagUnlockGroupOrderMutation['unlockOrder'] | undefined;
};
